import React, { useMemo } from 'react';
import { TbPlayerTrackNextFilled } from 'react-icons/tb';
import {
    Table,
    TableContainer,
    Tbody,
    Td,
    Th,
    Thead,
    Tr,
    Tooltip,
    useToast
} from '@chakra-ui/react';

import { useStore } from 'commons/hooks';
import { TransportRequestTag } from 'data/schemas/TransportRequest';
import { FaEdit } from 'react-icons/fa';
import { FaDownload } from 'react-icons/fa6';
import { FaCopy } from 'react-icons/fa';

type Props = {
    transportRequestTag: TransportRequestTag[];
    onClickDeploy: (tag: TransportRequestTag) => void;
    onClickShowModalEdit: (transportRequest: TransportRequestTag) => void;
    onClickDowloadFile: (uatTag: string) => void;
    onClickShowModalReadyForPrd: (uatTag: TransportRequestTag) => void;
};

function TransportRequestTable(props: Props) {
    const {
        transportRequestTag,
        onClickDeploy,
        onClickShowModalEdit,
        onClickDowloadFile,
        onClickShowModalReadyForPrd
    } = props;
    const { authentication } = useStore();
    const toast = useToast();

    const headers = useMemo(
        () => [
            'Copy',
            'Download',
            'Edit',
            'Deploy',
            'Ready For Prd',
            'UAT TAG',
            'Module',
            'Deployment Object',
            'Version',
            'Status',
            'Ticket',
            'Detail',
            'Remark',
            'Feature',
            'Core / Non Core',
            'Add / Edit',
            'Impact',
            'Risk Level',
            'Developer'
        ],
        []
    );

    const employeeData = authentication.masterEmployeeResponse;

    const CopyToClipboard = async (tagDetail: TransportRequestTag) => {
        const { uatTag, developer, details } = tagDetail;
        const detailsWithTextWrap = '"' + details.trim() + '"';
        const nullStringRegEx = new RegExp('null', 'g');

        const outputLines = tagDetail.transportRequestDetail.map((detail, index) => {
            const {
                module,
                deploymentObject,
                version,
                ticket,
                feature,
                coreOrNonCore,
                addOrEdit,
                impact,
                riskLevel
            } = detail;

            if (index === 0) {
                const fistRowClipboardMessage =
                    uatTag +
                    '\t' +
                    developer +
                    '\t' +
                    module.toUpperCase() +
                    '\t' +
                    deploymentObject +
                    '\t' +
                    version +
                    '\t' +
                    ticket +
                    '\t' +
                    detailsWithTextWrap +
                    '\t' +
                    feature +
                    '\t' +
                    coreOrNonCore +
                    '\t' +
                    addOrEdit +
                    '\t' +
                    impact +
                    '\t' +
                    riskLevel;

                return fistRowClipboardMessage.replace(nullStringRegEx, '');
            }

            const clipboardMessage =
                '\t' +
                '\t' +
                module.toUpperCase() +
                '\t' +
                deploymentObject +
                '\t' +
                version +
                '\t' +
                //ticket +
                '\t' +
                //details +
                '\t' +
                //feature +
                '\t' +
                coreOrNonCore +
                '\t' +
                addOrEdit +
                '\t' +
                //impact +
                '\t';
            //riskLevel;

            return clipboardMessage.replace(nullStringRegEx, '');
        });

        try {
            await navigator.clipboard.writeText(outputLines.join('\n'));
            toast({ title: 'Copied', status: 'success', duration: 2000 });
        } catch {
            toast({ title: 'Unable to copy', status: 'error', duration: 3000 });
        }
    };

    return (
        <TableContainer
            whiteSpace="wrap"
            className="mt-2"
        >
            <Table
                size="sm"
                variant="unstyled"
            >
                <Thead className="sticky top-0 z-10 bg-white shadow-sm">
                    <Tr>
                        {headers.map(header => (
                            <Th
                                key={header}
                                className="m-0 border p-2 text-center"
                                style={{
                                    borderColor:
                                        'rgb(212 212 212 / var(--tw-border-opacity)) !important'
                                }}
                            >
                                {header}
                            </Th>
                        ))}
                    </Tr>
                </Thead>
                <Tbody className="overflow-x-scroll">
                    {transportRequestTag?.length ? (
                        transportRequestTag.map((tag, rowIndex) => {
                            const rowSpan = tag.transportRequestDetail.length;

                            return tag.transportRequestDetail.map((detail, detailIndex) => (
                                <Tr
                                    key={tag.uatTag + detailIndex}
                                    className={`hover:bg-cyan-200 [&>td]:border [&>td]:border-neutral-300 ${detail.deployStatus === 'Online' && detail.deployCompany.split(',').length === 20 ? 'bg-green-50' : ''}`}
                                    onDoubleClick={() => {
                                        CopyToClipboard(tag);
                                    }}
                                >
                                    {detailIndex === 0 && (
                                        <React.Fragment>
                                            <Td
                                                rowSpan={rowSpan}
                                                className="align-middle px-2 py-0.5 text-center"
                                            >
                                                <Tooltip label="Copy this transport request">
                                                    <button
                                                        aria-label={`Copy ${tag.uatTag}`}
                                                        className="inline-flex h-8 w-8 items-center justify-center"
                                                        onClick={() => CopyToClipboard(tag)}
                                                    >
                                                        <FaCopy className="text-blue-500" />
                                                    </button>
                                                </Tooltip>
                                            </Td>
                                            <Td
                                                rowSpan={rowSpan}
                                                className="align-middle px-2 py-0.5 text-center"
                                            >
                                                <Tooltip label="Download all files">
                                                    <button
                                                        aria-label={`Download ${tag.uatTag}`}
                                                        className="inline-flex h-8 w-8 items-center justify-center"
                                                        onClick={() =>
                                                            onClickDowloadFile(tag.uatTag)
                                                        }
                                                    >
                                                        <FaDownload className="text-green-500" />
                                                    </button>
                                                </Tooltip>
                                            </Td>
                                            <Td
                                                rowSpan={rowSpan}
                                                className="align-middle px-2 py-0.5 text-center"
                                            >
                                                <Tooltip label="Edit transport request">
                                                    <button
                                                        aria-label={`Edit ${tag.uatTag}`}
                                                        className="inline-flex h-8 w-8 items-center justify-center"
                                                        onClick={() => onClickShowModalEdit(tag)}
                                                    >
                                                        <FaEdit className="text-yellow-500" />
                                                    </button>
                                                </Tooltip>
                                            </Td>
                                            <Td
                                                className="m-0 px-2 py-0.5 text-center"
                                                rowSpan={rowSpan}
                                            >
                                                <Tooltip
                                                    label={
                                                        (detail.deployStatus === 'Online' &&
                                                            detail.deployCompany.split(',')
                                                                .length === 20)
                                                            ? 'Deployment is already complete'
                                                            : employeeData?.type !== 'super_admin' &&
                                                                employeeData?.ctizenID !== tag.developer
                                                              ? 'Only the owner or super admin can deploy'
                                                              : 'Deploy transport request'
                                                    }
                                                >
                                                    <span>
                                                        <button
                                                            aria-label={`Deploy ${tag.uatTag}`}
                                                            onClick={() => onClickDeploy(tag)}
                                                            disabled={
                                                                (detail.deployStatus === 'Online' &&
                                                                    detail.deployCompany.split(',')
                                                                        .length === 20) ||
                                                                (employeeData?.type !== 'super_admin' &&
                                                                    employeeData?.ctizenID !== tag.developer)
                                                            }
                                                        >
                                                            <TbPlayerTrackNextFilled
                                                                size={24}
                                                                className={`m-0 p-0 ${
                                                            (detail.deployStatus === 'Online' &&
                                                                detail.deployCompany.split(',')
                                                                    .length === 20) ||
                                                            (employeeData?.type !== 'super_admin' &&
                                                                employeeData?.ctizenID !==
                                                                    tag.developer)
                                                                ? 'pointer-events-none text-gray-600'
                                                                : 'text-green-600'
                                                                }`}
                                                            />
                                                        </button>
                                                    </span>
                                                </Tooltip>
                                            </Td>
                                        </React.Fragment>
                                    )}
                                    <Td className="m-0 px-2 py-0.5 text-center">
                                        <input
                                            type="checkbox"
                                            defaultChecked={detail.readyForPrd}
                                            checked={detail.readyForPrd}
                                            disabled={detail.readyForPrd}
                                            onClick={() => onClickShowModalReadyForPrd(tag)}
                                        />
                                    </Td>
                                    {detailIndex === 0 && (
                                        <Td
                                            rowSpan={rowSpan}
                                            className="m-0 whitespace-nowrap text-nowrap px-2 py-0.5"
                                        >
                                            {tag.uatTag}
                                        </Td>
                                    )}
                                    <Td className="m-0 px-2 py-0.5 text-center">
                                        <span>{detail.module.toUpperCase()}</span>
                                    </Td>
                                    <Td className="m-0 max-w-[460px] truncate px-2 py-0.5">
                                        <Tooltip
                                            label={detail.deploymentObject}
                                            placement="top"
                                        >
                                            <span>{detail.deploymentObject}</span>
                                        </Tooltip>
                                    </Td>
                                    <Td className="m-0 px-2 py-0.5 text-left">
                                        <span>{detail.version}</span>
                                    </Td>
                                    <Td
                                        className={`m-0 px-2 py-0.5 text-center ${
                                            detail.deployStatus === 'Online'
                                                ? 'font-bold text-green-600'
                                                : 'font-bold text-yellow-500'
                                        }`}
                                    >
                                        {detail.deployStatus}
                                    </Td>
                                    {detailIndex === 0 && (
                                        <React.Fragment>
                                            <Td
                                                rowSpan={rowSpan}
                                                className="m-0 max-w-[460px] truncate px-2 py-0.5"
                                            >
                                                <Tooltip
                                                    label={detail.ticket}
                                                    placement="top-start"
                                                >
                                                    <span>{detail.ticket}</span>
                                                </Tooltip>
                                            </Td>
                                            <Td
                                                rowSpan={rowSpan}
                                                className="m-0 max-w-[460px] truncate px-2 py-0.5"
                                            >
                                                <Tooltip
                                                    label={tag.details}
                                                    placement="top-start"
                                                >
                                                    <span>{tag.details}</span>
                                                </Tooltip>
                                            </Td>
                                            <Td
                                                className="m-0 min-w-[150px] max-w-[460px] truncate px-2 py-0.5 text-red-400"
                                                rowSpan={rowSpan}
                                            >
                                                <Tooltip
                                                    label={detail.remark}
                                                    placement="top-start"
                                                >
                                                    <span>{detail.remark}</span>
                                                </Tooltip>
                                            </Td>
                                            <Td
                                                rowSpan={rowSpan}
                                                className="m-0 px-2 py-0.5"
                                            >
                                                {detail.feature}
                                            </Td>
                                        </React.Fragment>
                                    )}
                                    <Td className="m-0 px-2 py-0.5">{detail.coreOrNonCore}</Td>
                                    <Td className="m-0 px-2 py-0.5">{detail.addOrEdit}</Td>
                                    {detailIndex === 0 && (
                                        <React.Fragment>
                                            <Td
                                                rowSpan={rowSpan}
                                                className="m-0 w-[460px] truncate px-2 py-0.5"
                                            >
                                                <Tooltip
                                                    label={detail.impact}
                                                    placement="top-start"
                                                >
                                                    <span>{detail.impact}</span>
                                                </Tooltip>
                                            </Td>
                                            <Td
                                                className="m-0 px-2 py-0.5 text-center"
                                                rowSpan={rowSpan}
                                            >
                                                {detail.riskLevel}
                                            </Td>
                                            <Td
                                                className="m-0 px-2 py-0.5"
                                                rowSpan={rowSpan}
                                            >
                                                {tag.developer}
                                            </Td>
                                        </React.Fragment>
                                    )}
                                </Tr>
                            ));
                        })
                    ) : (
                        <Tr>
                            <Td
                                colSpan={headers.length}
                                className="py-12 text-center text-gray-500"
                            >
                                No transport requests match the selected filters.
                            </Td>
                        </Tr>
                    )}
                </Tbody>
            </Table>
        </TableContainer>
    );
}

export default TransportRequestTable;
